/**
 * @file routes/(public)/index.tsx
 * @description 對外公開首頁 (`/`).
 *
 * 設計方向：「Quiet Refinement」 — 與系統內部頁面同調，但有 landing 級的留白與層次。
 * 結構：
 *   1. Header — 細邊條 + 品牌名 + 內部登入入口
 *   2. Hero — 左文案右抽象六邊形圖案（驗證印章意象）
 *   3. 信任條 — 累計獲證 / 本月有效 / 平均工作日 / 認可標準
 *   4. 三方案 — TOP / TAP / ECO 的卡片陣列，編號 mono 字體 + 圖標 + 簡介 + metric
 *   5. 客戶案例 — 三張案例故事卡（占位圖 + 客戶 + 引言 + 方案 + metric）
 *   6. 現場實拍 — 4×2 八宮格畫廊，地點 + 場景標籤（占位 SVG，後續替換真實照片）
 *   7. 證書查詢入口 — 寬搜索條 + 三個範例 chip
 *   8. 聯絡我們 — 左欄聯絡資訊（電話/Email/地址/營業時間）+ 右欄諮詢表單
 *   9. Footer — 品牌摘要 + 站內導覽 + 員工登入 + 版權
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Leaf, ShieldCheck, Sparkles, ArrowRight, Search } from 'lucide-react';

import { CaseStoryCard } from '@/components/home/CaseStoryCard';
import { ContactSection } from '@/components/home/ContactSection';
import { FieldGalleryItem } from '@/components/home/FieldGalleryItem';
import { CASE_STORIES, FIELD_GALLERY } from '@/components/home/home-data';

export const Route = createFileRoute('/(public)/')({ component: HomePage });

const SCHEMES = [
  {
    code: 'TOP',
    label: '有機農產品',
    title: 'TOP / 有機',
    description: '依《有機農業促進法》，從種植、加工到流通的全鏈路稽核與認可。',
    metric: '12 項',
    metricLabel: '稽核項目',
    icon: Leaf,
    accent: 'var(--forest-600)',
    bg: 'var(--forest-50)',
  },
  {
    code: 'TAP',
    label: '產銷履歷',
    title: 'TAP / 產銷履歷',
    description: '依《農產品生產及驗證管理法》，建立可追溯的生產履歷與品質證明。',
    metric: '4 大類別',
    metricLabel: '農糧／加工／分裝／流通',
    icon: ShieldCheck,
    accent: 'var(--stone-700)',
    bg: 'var(--stone-100)',
  },
  {
    code: 'ECO',
    label: '友善農業',
    title: 'ECO / 友善',
    description: '針對非有機但採行友善環境耕作之農場提供獨立驗證與標章。',
    metric: '180 天',
    metricLabel: '完整作業期',
    icon: Sparkles,
    accent: 'var(--forest-700)',
    bg: 'var(--forest-100)',
  },
] as const;

const SEARCH_EXAMPLES = ['CERT-2026-TOP0001-01', '宜蘭水稻有機農場', '有機'];

function HomePage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  const submitSearch = () => {
    const trimmed = q.trim();
    if (!trimmed) {
      void navigate({ to: '/public/cert-search' });
      return;
    }
    void navigate({ to: '/public/cert-search', search: { q: trimmed } });
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--surface-canvas)',
        color: 'var(--stone-900)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ===== Header ===== */}
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'rgba(249, 249, 248, 0.85)',
          backdropFilter: 'blur(12px) saturate(180%)',
          borderBottom: '1px solid var(--stone-200)',
        }}
      >
        <div style={layoutContainerStyle('24px 32px')}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
              <BrandWordmark />
            </Link>
            <nav style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
              <a href="#schemes" style={navLinkStyle}>
                驗證方案
              </a>
              <a href="#cases" style={navLinkStyle}>
                客戶案例
              </a>
              <a href="#search" style={navLinkStyle}>
                證書查詢
              </a>
              <a href="#contact" style={navLinkStyle}>
                聯絡
              </a>
              <Link
                to="/login"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 16px',
                  fontSize: 13,
                  fontWeight: 500,
                  color: 'var(--surface-base)',
                  background: 'var(--stone-900)',
                  borderRadius: 8,
                  textDecoration: 'none',
                  transition: 'all 150ms ease-out',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--forest-700)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--stone-900)')}
              >
                員工登入
                <ArrowRight size={14} />
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* ===== Hero ===== */}
      <section style={{ padding: '88px 0 64px' }}>
        <div style={layoutContainerStyle('0 32px')}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr)',
              gap: 64,
              alignItems: 'center',
            }}
          >
            {/* Left: copy */}
            <div>
              <span
                style={{
                  display: 'inline-block',
                  padding: '4px 10px',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: 'var(--forest-700)',
                  background: 'var(--forest-50)',
                  border: '1px solid var(--forest-200)',
                  borderRadius: 999,
                  marginBottom: 24,
                }}
              >
                ZHCERT · 中華驗證有限公司
              </span>
              <h1
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'clamp(40px, 5vw, 64px)',
                  lineHeight: 1.08,
                  fontWeight: 600,
                  letterSpacing: '-0.02em',
                  color: 'var(--stone-900)',
                  margin: '0 0 24px',
                }}
              >
                為臺灣農產品提供
                <br />
                公正、可追溯的驗證
              </h1>
              <p
                style={{
                  fontSize: 17,
                  lineHeight: 1.65,
                  color: 'var(--stone-600)',
                  maxWidth: 520,
                  margin: '0 0 36px',
                }}
              >
                從生產、加工、分裝到流通，建立每一份證書背後可被檢視的稽核軌跡。
                以國際公認標準為基礎，讓食品安全與品質成為可被驗證的承諾。
              </p>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <a
                  href="#search"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '14px 24px',
                    fontSize: 15,
                    fontWeight: 500,
                    color: 'var(--surface-base)',
                    background: 'var(--forest-600)',
                    borderRadius: 10,
                    textDecoration: 'none',
                    transition: 'all 150ms ease-out',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--forest-700)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--forest-600)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  查詢證書
                  <Search size={16} />
                </a>
                <a
                  href="#schemes"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '14px 20px',
                    fontSize: 15,
                    fontWeight: 500,
                    color: 'var(--stone-700)',
                    background: 'transparent',
                    border: '1px solid var(--stone-300)',
                    borderRadius: 10,
                    textDecoration: 'none',
                    transition: 'all 150ms ease-out',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--stone-500)';
                    e.currentTarget.style.color = 'var(--stone-900)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--stone-300)';
                    e.currentTarget.style.color = 'var(--stone-700)';
                  }}
                >
                  了解驗證方案
                </a>
              </div>
            </div>

            {/* Right: hexagon-stamp SVG */}
            <div style={{ position: 'relative', height: 420 }}>
              <HexStampArt />
            </div>
          </div>
        </div>
      </section>

      {/* ===== Trust strip ===== */}
      <section
        style={{
          padding: '32px 0',
          borderTop: '1px solid var(--stone-200)',
          borderBottom: '1px solid var(--stone-200)',
        }}
      >
        <div style={layoutContainerStyle('0 32px')}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 32,
            }}
          >
            <TrustItem label="累計獲證組織" value="2,184" />
            <TrustItem label="本月有效證書" value="1,672" />
            <TrustItem label="平均處理工作日" value="180" />
            <TrustItem label="認可標準依據" value="3 項" />
          </div>
        </div>
      </section>

      {/* ===== Schemes ===== */}
      <section id="schemes" style={{ padding: '96px 0' }}>
        <div style={layoutContainerStyle('0 32px')}>
          <SectionHeader
            title="三個標準，覆蓋台灣農產品品質要求"
            description="依產品屬性與市場目標選擇對應方案，每套方案均含現場稽核、文件審查、樣品檢測與委員會決定。"
          />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 24,
              marginTop: 56,
            }}
          >
            {SCHEMES.map((s, idx) => (
              <SchemeCard key={s.code} scheme={s} index={idx + 1} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== Case stories ===== */}
      {/*
       * 案例故事：三張大卡，上方為實拍照片（CASE 編號與方案 chip 烘焙在圖內），下方為客戶 / 引言 / metric。
       * 設計目的：在驗證方案的「能力說明」之後，用具體案例佐證「實際發生」的事實。
       */}
      <section
        id="cases"
        style={{
          padding: '96px 0',
          background: 'var(--surface-base)',
          borderTop: '1px solid var(--stone-200)',
        }}
      >
        <div style={layoutContainerStyle('0 32px')}>
          <SectionHeader
            title="他們選擇了 ZHCERT"
            description="從西部稻米到東岸果園，每一份證書背後都有一段被獨立稽核的生產故事。"
          />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 24,
              marginTop: 56,
            }}
          >
            {CASE_STORIES.map((story) => (
              <CaseStoryCard key={story.code} story={story} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== Field gallery ===== */}
      {/*
       * 現場實拍畫廊：4×2 八宮格，覆蓋「田間 / 抽樣 / 加工 / 包裝 / 巡查 / 文件 / 委員會 / 證書」八個稽核關鍵節點。
       * 場景圖示、場景名與地點已烘焙在圖片內，hover 時微微浮起。
       */}
      <section
        id="field-gallery"
        style={{
          padding: '96px 0',
          background: 'var(--stone-100)',
          borderTop: '1px solid var(--stone-200)',
          borderBottom: '1px solid var(--stone-200)',
        }}
      >
        <div style={layoutContainerStyle('0 32px')}>
          <SectionHeader
            title="現場稽核軌跡"
            description="從產地到加工廠，從抽樣到文件審查 — 每一次稽核都留下可被回溯的影像紀錄。"
          />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 14,
              marginTop: 48,
            }}
          >
            {FIELD_GALLERY.map((item) => (
              <FieldGalleryItem key={item.label + item.sub} item={item} />
            ))}
          </div>
          <p
            style={{
              marginTop: 28,
              fontSize: 12,
              fontFamily: 'var(--font-mono)',
              letterSpacing: '0.08em',
              color: 'var(--stone-500)',
              textAlign: 'center',
            }}
          >
            以上為示意素材，實際稽核影像依當事客戶授權範圍陳列。
          </p>
        </div>
      </section>

      {/* ===== Search ===== */}
      <section
        id="search"
        style={{
          padding: '88px 0',
          background: 'var(--stone-100)',
          borderTop: '1px solid var(--stone-200)',
          borderBottom: '1px solid var(--stone-200)',
        }}
      >
        <div style={layoutContainerStyle('0 32px')}>
          <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
            <SectionHeader
              align="center"
              title="輸入證書編號或客戶名稱"
              description="任何人皆可查詢由 ZHCERT 核發的有效證書資訊。"
            />
            <div
              style={{
                marginTop: 40,
                display: 'flex',
                gap: 0,
                background: 'var(--surface-base)',
                border: '1px solid var(--stone-300)',
                borderRadius: 12,
                padding: 6,
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              }}
            >
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitSearch();
                }}
                placeholder="例：CERT-2026-TOP0001-01"
                style={{
                  flex: 1,
                  padding: '14px 18px',
                  fontSize: 16,
                  fontFamily: 'var(--font-body)',
                  color: 'var(--stone-900)',
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                }}
              />
              <button
                onClick={submitSearch}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '12px 22px',
                  fontSize: 15,
                  fontWeight: 500,
                  color: 'var(--surface-base)',
                  background: 'var(--forest-600)',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  transition: 'background 150ms ease-out',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--forest-700)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--forest-600)')}
              >
                <Search size={16} />
                查詢
              </button>
            </div>
            <div
              style={{
                marginTop: 20,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--stone-500)',
                }}
              >
                範例
              </span>
              {SEARCH_EXAMPLES.map((ex) => (
                <button
                  key={ex}
                  onClick={() => {
                    setQ(ex);
                    void navigate({ to: '/public/cert-search', search: { q: ex } });
                  }}
                  style={{
                    padding: '4px 10px',
                    fontSize: 12,
                    fontFamily: 'var(--font-mono)',
                    color: 'var(--stone-700)',
                    background: 'var(--surface-base)',
                    border: '1px solid var(--stone-300)',
                    borderRadius: 999,
                    cursor: 'pointer',
                    transition: 'all 150ms ease-out',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--forest-500)';
                    e.currentTarget.style.color = 'var(--forest-700)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--stone-300)';
                    e.currentTarget.style.color = 'var(--stone-700)';
                  }}
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== Contact ===== */}
      {/*
       * 對外聯絡分區：左欄列出電話 / Email / 地址 / 營業時間，右欄為諮詢表單。
       * 持有 `#contact` 錨點，讓導覽列「聯絡」按鈕滾動至此處（先前指向 footer）。
       */}
      <ContactSection />

      {/* ===== Footer ===== */}
      <footer
        style={{
          marginTop: 'auto',
          padding: '64px 0 32px',
          background: 'var(--stone-900)',
          color: 'var(--stone-400)',
        }}
      >
        <div style={layoutContainerStyle('0 32px')}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1fr 1fr',
              gap: 48,
              marginBottom: 48,
            }}
          >
            <div>
              <BrandWordmark dark />
              <p
                style={{
                  marginTop: 16,
                  fontSize: 13,
                  lineHeight: 1.65,
                  color: 'var(--stone-500)',
                  maxWidth: 360,
                }}
              >
                ZHCERT 中華驗證有限公司
                <br />
                獨立第三方農產品驗證機構，依國際標準與臺灣法規提供有機、產銷履歷與友善農業驗證服務。
              </p>
            </div>
            <FooterColumn
              title="驗證方案"
              items={[
                { label: 'TOP 有機', href: '#schemes' },
                { label: 'TAP 產銷履歷', href: '#schemes' },
                { label: 'ECO 友善', href: '#schemes' },
              ]}
            />
            <FooterColumn
              title="服務"
              items={[
                { label: '證書查詢', href: '#search' },
                { label: '申請流程', href: '#schemes' },
                { label: '常見問題', href: '#contact' },
              ]}
            />
            <FooterColumn
              title="聯絡"
              items={[
                { label: '客服信箱', href: 'mailto:zh.cert@msa.hinet.net' },
                { label: '電話 049-2568787', href: 'tel:0492568787' },
                { label: '員工登入', to: '/login' },
              ]}
            />
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: 24,
              borderTop: '1px solid var(--stone-800)',
              fontSize: 12,
              color: 'var(--stone-500)',
              fontFamily: 'var(--font-mono)',
              letterSpacing: '0.04em',
            }}
          >
            <span>© 2026 ZHCERT · CHUNG-HUA CERTIFICATION CORP.</span>
            <span>v0.2.0</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ============ helpers ============ */

function layoutContainerStyle(padding: string): React.CSSProperties {
  return {
    maxWidth: 1200,
    margin: '0 auto',
    padding,
    width: '100%',
    boxSizing: 'border-box',
  };
}

const navLinkStyle: React.CSSProperties = {
  fontSize: 14,
  color: 'var(--stone-700)',
  textDecoration: 'none',
  transition: 'color 150ms ease-out',
};

function BrandWordmark({ dark = false }: { dark?: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        fontFamily: 'var(--font-display)',
        fontWeight: 600,
        fontSize: 22,
        letterSpacing: '-0.01em',
        color: dark ? 'var(--surface-base)' : 'var(--stone-900)',
      }}
    >
      {/* Icon logo：原圖為純黑線稿；在深色頁尾用 filter 反白，保持與底色對比 */}
      <img
        src="/zhcert-logo-icon.png"
        alt=""
        aria-hidden="true"
        style={{
          width: 28,
          height: 28,
          display: 'block',
          objectFit: 'contain',
          filter: dark ? 'invert(1)' : 'none',
        }}
      />
      <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>ZHCERT</span>
    </span>
  );
}

function HexStampArt() {
  return (
    <svg viewBox="0 0 480 480" style={{ width: '100%', height: '100%' }} role="presentation">
      <defs>
        <linearGradient id="hexGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--forest-100)" />
          <stop offset="100%" stopColor="var(--forest-200)" />
        </linearGradient>
        <radialGradient id="hexGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="var(--forest-500)" stopOpacity="0.12" />
          <stop offset="100%" stopColor="var(--forest-500)" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* glow */}
      <circle cx="240" cy="240" r="220" fill="url(#hexGlow)" />
      {/* outer concentric hex (dotted) */}
      {[200, 160, 120].map((r, i) => (
        <polygon
          key={i}
          points={hexPoints(240, 240, r)}
          fill="none"
          stroke="var(--forest-300)"
          strokeWidth={i === 2 ? 1.5 : 1}
          strokeDasharray={i === 0 ? '4 6' : i === 1 ? '6 8' : 'none'}
          opacity={0.5 + i * 0.15}
        />
      ))}
      {/* main hex with gradient */}
      <polygon
        points={hexPoints(240, 240, 90)}
        fill="url(#hexGrad)"
        stroke="var(--forest-600)"
        strokeWidth="1.5"
      />
      {/* center monogram */}
      <text
        x="240"
        y="252"
        textAnchor="middle"
        fontFamily="var(--font-display)"
        fontWeight="600"
        fontSize="48"
        fill="var(--forest-700)"
        letterSpacing="-1"
      >
        ZH
      </text>
      {/* corner marks */}
      {hexPoints(240, 240, 200)
        .split(' ')
        .map((p, i) => {
          const [cx, cy] = p.split(',').map(Number);
          return <circle key={i} cx={cx} cy={cy} r="3.5" fill="var(--forest-600)" />;
        })}
      {/* connecting tick marks */}
      {[0, 60, 120, 180, 240, 300].map((angle, i) => {
        const rad = (angle - 90) * (Math.PI / 180);
        const x1 = 240 + Math.cos(rad) * 95;
        const y1 = 240 + Math.sin(rad) * 95;
        const x2 = 240 + Math.cos(rad) * 110;
        const y2 = 240 + Math.sin(rad) * 110;
        return (
          <line
            key={i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="var(--forest-600)"
            strokeWidth="1.5"
          />
        );
      })}
    </svg>
  );
}

function hexPoints(cx: number, cy: number, r: number): string {
  return Array.from({ length: 6 }, (_, i) => {
    const angle = (i * 60 - 90) * (Math.PI / 180);
    return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`;
  }).join(' ');
}

function TrustItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'var(--stone-500)',
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 28,
          fontWeight: 600,
          color: 'var(--stone-900)',
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.01em',
        }}
      >
        {value}
      </div>
    </div>
  );
}

function SectionHeader({
  title,
  description,
  align = 'left',
}: {
  title: string;
  description?: string;
  align?: 'left' | 'center';
}) {
  return (
    <div
      style={{
        textAlign: align,
        maxWidth: align === 'center' ? 720 : undefined,
        margin: align === 'center' ? '0 auto' : undefined,
      }}
    >
      <h2
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'clamp(28px, 3vw, 36px)',
          fontWeight: 600,
          letterSpacing: '-0.015em',
          color: 'var(--stone-900)',
          lineHeight: 1.2,
          margin: 0,
        }}
      >
        {title}
      </h2>
      {description && (
        <p
          style={{
            marginTop: 14,
            fontSize: 15,
            lineHeight: 1.65,
            color: 'var(--stone-600)',
            maxWidth: align === 'center' ? 540 : 620,
            marginInline: align === 'center' ? 'auto' : undefined,
          }}
        >
          {description}
        </p>
      )}
    </div>
  );
}

function SchemeCard({ scheme, index }: { scheme: (typeof SCHEMES)[number]; index: number }) {
  const Icon = scheme.icon;
  return (
    <div
      style={{
        position: 'relative',
        padding: '32px 28px',
        background: 'var(--surface-base)',
        border: '1px solid var(--stone-200)',
        borderRadius: 12,
        transition: 'all 200ms ease-out',
        cursor: 'default',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.borderColor = 'var(--stone-300)';
        e.currentTarget.style.boxShadow = '0 12px 28px -16px rgba(0,0,0,0.12)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.borderColor = 'var(--stone-200)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 28,
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 12,
            letterSpacing: '0.08em',
            color: 'var(--stone-500)',
          }}
        >
          0{index} / {SCHEMES.length.toString().padStart(2, '0')}
        </span>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            background: scheme.bg,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon size={22} color={scheme.accent} strokeWidth={1.5} />
        </div>
      </div>

      <div
        style={{
          marginBottom: 8,
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: scheme.accent,
        }}
      >
        {scheme.code} · {scheme.label}
      </div>
      <h3
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 22,
          fontWeight: 600,
          letterSpacing: '-0.01em',
          color: 'var(--stone-900)',
          margin: '0 0 12px',
        }}
      >
        {scheme.title}
      </h3>
      <p
        style={{
          fontSize: 14,
          lineHeight: 1.65,
          color: 'var(--stone-600)',
          margin: '0 0 24px',
          minHeight: 68,
        }}
      >
        {scheme.description}
      </p>

      <div
        style={{
          paddingTop: 20,
          borderTop: '1px solid var(--stone-150)',
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 26,
            fontWeight: 600,
            color: 'var(--stone-900)',
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '-0.01em',
          }}
        >
          {scheme.metric}
        </span>
        <span style={{ fontSize: 12, color: 'var(--stone-500)' }}>{scheme.metricLabel}</span>
      </div>
    </div>
  );
}

function FooterColumn({
  title,
  items,
}: {
  title: string;
  items: { label: string; href?: string; to?: string }[];
}) {
  return (
    <div>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'var(--stone-300)',
          marginBottom: 16,
        }}
      >
        {title}
      </div>
      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {items.map((item) => (
          <li key={item.label}>
            {item.to ? (
              <Link
                to={item.to}
                style={{
                  fontSize: 13,
                  color: 'var(--stone-400)',
                  textDecoration: 'none',
                  transition: 'color 150ms ease-out',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--surface-base)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--stone-400)')}
              >
                {item.label}
              </Link>
            ) : (
              <a
                href={item.href}
                style={{
                  fontSize: 13,
                  color: 'var(--stone-400)',
                  textDecoration: 'none',
                  transition: 'color 150ms ease-out',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--surface-base)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--stone-400)')}
              >
                {item.label}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
